/**
 * Pricing Summary Component
 *
 * Displays booking pricing breakdown with platform fee
 * Shows per-person and total pricing
 *
 * Location: src/components/booking/flow/PricingSummary.jsx
 * Epic: MVP 1.4 - Customer Booking Flow
 *
 * Features:
 * - Per-person pricing display
 * - Subtotal calculation
 * - Platform fee (8%)
 * - Total price
 * - Responsive layout
 */

import React from 'react';
import { formatCentsToDollars } from '../../../utils/formHelpers';

/**
 * PricingSummary Component
 *
 * @param {Object} props
 * @param {number} props.perPersonCents - Price per person in cents
 * @param {number} props.numParticipants - Number of participants
 * @param {number} props.subtotalCents - Subtotal in cents
 * @param {number} props.platformFeeCents - Platform fee in cents (8%)
 * @param {number} [props.taxAmountCents=0] - Sales tax in cents (hidden when 0)
 * @param {number} props.totalCents - Total price in cents
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element}
 */
const PricingSummary = ({
  perPersonCents,
  numParticipants = 1,
  subtotalCents,
  platformFeeCents,
  taxAmountCents = 0,
  totalCents,
  className = ''
}) => {
  return (
    <div className={`bg-gray-50 rounded-lg p-4 space-y-2 ${className}`}>
      <h3 className="font-semibold text-gray-900 mb-3">Pricing Summary</h3>

      {/* Per Person */}
      <div className="flex justify-between text-sm">
        <span className="text-gray-600">
          {formatCentsToDollars(perPersonCents)} × {numParticipants} {numParticipants === 1 ? 'guest' : 'guests'}
        </span>
        <span className="text-gray-900">{formatCentsToDollars(subtotalCents)}</span>
      </div>

      {/* Platform Fee - SCRUM-337: Standardized terminology */}
      <div className="flex justify-between text-sm">
        <span className="text-gray-600">Service fee</span>
        <span className="text-gray-900">{formatCentsToDollars(platformFeeCents)}</span>
      </div>

      {/* Sales Tax — SCRUM-2057: hidden when zero */}
      {taxAmountCents > 0 && (
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Sales tax</span>
          <span className="text-gray-900">{formatCentsToDollars(taxAmountCents)}</span>
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-gray-200 pt-2 mt-2"></div>

      {/* Total */}
      <div className="flex justify-between font-semibold text-base">
        <span className="text-gray-900">Total</span>
        <span className="text-gray-900">{formatCentsToDollars(totalCents)}</span>
      </div>
    </div>
  );
};

export default PricingSummary;
